<template>
    <div data-component="RedeemGroupButton">
        <!-- Redeem / Unredeem All Button -->
        <button v-if="isGroupRedeemable && currentGroupRedeemAllButtonIsRedemption === true"
                data-testid="redeemCurrentGroupButton"
                type="button"
                class="btn btn--redeem-group"
                :class="[currentGroupRedeemAllButtonColorClass]"
                :disabled="isRedeemCurrentGroupDisabled"
                @click.prevent="redeemCurrentGroup">
            <span v-if="group.itemType === 'Ticket' && !group.session">
                {{ l('ORDER_DETAILS.REDEEM_ALL_OPEN_TICKETS') }}
            </span>
            <span v-else-if="group.itemType === 'Ticket' && group.session">
                {{ l('ORDER_DETAILS.REDEEM_ALL_TICKETS') }}
                <small>
                    {{ l('ORDER_DETAILS.REDEEM_ALL_TICKETS__FOR') }}
                    {{ timeFilter(group.session.startTime, true) }}

                    <!-- If this session is in a different timezone display a warning indicator. -->
                    <i v-if="group.isDifferentTimezone"
                       class="fa-solid fa-circle-exclamation"></i>
                </small>
            </span>
            <span v-else-if="group.itemType === 'Membership Plan'">
                {{ l('ORDER_DETAILS.REDEEM_ALL_MEMBERSHIPS') }}
            </span>
        </button>

        <button v-if="isGroupRedeemable && currentGroupRedeemAllButtonIsRedemption === false"
                data-testid="unredeemCurrentGroupButton"
                type="button"
                class="btn btn-danger btn--redeem-group btn--unredeem-group"
                :disabled="isRedeemCurrentGroupDisabled"
                @click.prevent="unredeemCurrentGroup">
            <span v-if="group.itemType === 'Ticket' && !group.session">
                {{ l('ORDER_DETAILS.UNREDEEM_ALL_OPEN_TICKETS') }}
            </span>
            <span v-else-if="group.itemType === 'Ticket' && group.session">
                {{ l('ORDER_DETAILS.UNREDEEM_ALL_TICKETS') }}
                <small>
                    {{ l('ORDER_DETAILS.REDEEM_ALL_TICKETS__FOR') }}
                    {{ timeFilter(group.session.startTime, true) }}

                    <!-- If this session is in a different timezone display a warning indicator. -->
                    <i v-if="group.isDifferentTimezone"
                       class="fa-solid fa-circle-exclamation"></i>
                </small>
            </span>
            <span v-else-if="group.itemType === 'Membership Plan'">
                {{ l('ORDER_DETAILS.UNREDEEM_ALL_MEMBERSHIPS') }}
            </span>
        </button>
    </div>
</template>

<script>
import EventNames from '@/lib/Events/EventNames';
import ItemType from '@/lib/Items/ItemType';
import { container } from '@/container';
import {
    isCurrentGroupForSessionTodayCheck,
    isGroupRedeemableCheck,
    redemptionEnabledForOrderLineCheck,
} from '@/epos/vue/pages/order-details/functions/redemptionFunctions';

export default {
    name: 'RedeemGroupButton',

    props: {
        group: {
            type: Object,
            required: true,
        },
        isLiveOrder: {
            type: Boolean,
            required: true,
        },
    },

    computed: {
        isGroupRedeemable() {
            return isGroupRedeemableCheck(this.group, this.isLiveOrder);
        },

        /**
         * Should the "redeem all" button for the group do a redemption or an unredemption.
         * If there are any unredeemed tickets it will show 'Redeem All Available' (true).
         * If all tickets are redeemed it will show 'Unredeem All Available' (false).
         *
         * @return {boolean|null} true = redemption, false = unredemption, null = don't show the button.
         */
        currentGroupRedeemAllButtonIsRedemption() {
            if (this.group.itemType === ItemType.MEMBERSHIP_PLAN) {
                // If there are any members on any memberships in the group that
                // have not been redeemed today it will be a redemption.

                let hasRedeemedMembers = false;

                for (let i = 0; i < this.group.items.length; i++) {
                    let orderLine = this.group.items[i];
                    for (let j = 0; j < orderLine.iteminstances.length; j++) {
                        let itemInstance = orderLine.iteminstances[j];
                        if (itemInstance.membership) {
                            if (itemInstance.membership.hasNonRedeemedMembersToday() && !orderLine.isExpired()) {
                                return true;
                            }
                            if (itemInstance.membership.hasRedeemedMembersToday()) {
                                hasRedeemedMembers = true;
                            }
                        }
                    }
                }

                // If we get this far there are no remaining not-redeemed members.
                // If there are some redeemed we'll show an unredeem button.
                // If not we'll return null so no button shows.
                return hasRedeemedMembers ? false : null;
            }

            if (this.group.itemType === ItemType.TICKET) {
                for (let i = 0; i < this.group.items.length; i++) {
                    let orderLine = this.group.items[i];
                    if (orderLine.hasIndividualTicketInstances() && !orderLine.isExpired()) {
                        if (orderLine.summaryItemInstanceNetRedemptions() < orderLine.qty) {
                            return true;
                        }
                    } else if (orderLine.redeemed < orderLine.qty && !orderLine.isExpired()) {
                        return true;
                    }
                }
            }
            return false;
        },

        // Simplified check for button disabled state
        isRedeemCurrentGroupDisabled() {
            // If any of the lines in the group cannot be redeemed (probably due to waivers), return false.
            for (let i = 0; i < this.group.items.length; i++) {
                if (!redemptionEnabledForOrderLineCheck(this.group.items[i])) {
                    // Redemption is not allowed for the current line, so *disable* the button:
                    return true;
                }
            }

            return !!this.group.items.find((orderLine) => {
                return orderLine.redemptionIsInProgress()
                    || orderLine.redemptionError
                    || !!orderLine.iteminstances.find((itemInstance) => {
                        return itemInstance.redemptionIsInProgress() || itemInstance.redemptionError;
                    });
            }) || this.group.items.every((orderLine) => orderLine.qty <= 0); // Every line is a refund.
        },

        /**
         * Returns the class to set the colour of the "redeem all" button for the whole group/tab.
         * - red/danger = all lines are already fully redeemed
         * - orange/warning = at least one line has some more redemptions available, but the session is not today
         * - green/success = at least one line has some more redemptions available
         *
         * @see OrderLine.getRedeemButtonColorClass for the same function for each line.
         *
         * @return {string}
         */
        currentGroupRedeemAllButtonColorClass() {
            if (!this.isGroupRedeemable) {
                // Nothing is left redeemable, so this will be an un-redemption. Show a red button.
                return 'btn-danger';
            }

            if (this.isCurrentGroupForSessionToday === false) {
                return 'btn-warning';
            }

            return 'btn-success';
        },

        isCurrentGroupForSessionToday() {
            return isCurrentGroupForSessionTodayCheck(this.group);
        },
    },
    methods: {
        redeemCurrentGroup() {
            container.eventBus.broadcast(
                EventNames.REDEEM_GROUP_BUTTON_REDEEM_GROUP,
                this.group.ID
            );
        },

        unredeemCurrentGroup() {
            container.eventBus.broadcast(
                EventNames.REDEEM_GROUP_BUTTON_UNREDEEM_GROUP,
                this.group.ID
            );
        },
    },
};
</script>
